import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { clientApi, Pack, CompanyGroup } from '@/api/client'
import { getDefaultWatchlistId } from '@/auth/session'

type Step = 1 | 2 | 3 | 4

export default function Onboarding() {
  const nav = useNavigate()
  const [step, setStep] = useState<Step>(1)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Step 1
  const [orgName, setOrgName] = useState('')

  // Step 2
  const [packs, setPacks] = useState<Pack[]>([])
  const [selectedPacks, setSelectedPacks] = useState<Set<string>>(new Set())
  const [packsLoaded, setPacksLoaded] = useState(false)
  const [packsLoadError, setPacksLoadError] = useState<string | null>(null)

  // Step 3
  const [groups, setGroups] = useState<CompanyGroup[]>([])
  const [selectedGroups, setSelectedGroups] = useState<Set<string>>(new Set())
  const [groupsLoaded, setGroupsLoaded] = useState(false)
  const [groupsLoadError, setGroupsLoadError] = useState<string | null>(null)

  // Step 4
  const [question, setQuestion] = useState('')
  const [asked, setAsked] = useState(false)

  useEffect(() => {
    // Pre-fetch org name
    clientApi.org().then(r => {
      if (r?.org?.name) setOrgName(r.org.name)
    }).catch(() => {})
  }, [])

  useEffect(() => {
    if (step === 2 && !packsLoaded) {
      setPacksLoadError(null)
      clientApi.packs().then(p => {
        const list = Array.isArray(p) ? p : []
        setPacks(list)
        setPacksLoaded(true)
        // Pre-select first 2
        const initial = new Set<string>()
        list.slice(0, 2).forEach(pk => initial.add(pk.pack_id))
        setSelectedPacks(initial)
      }).catch((e: any) => {
        setPacksLoaded(true)
        setPacksLoadError(e?.message || 'Failed to load packs')
      })
    }
    if (step === 3 && !groupsLoaded) {
      setGroupsLoadError(null)
      clientApi.companyGroups().then(r => {
        setGroups(r?.groups || [])
        setGroupsLoaded(true)
      }).catch((e: any) => {
        setGroupsLoaded(true)
        setGroupsLoadError(e?.message || 'Failed to load groups')
      })
    }
  }, [step, packsLoaded, groupsLoaded])

  function togglePack(id: string) {
    setSelectedPacks(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleGroup(id: string) {
    setSelectedGroups(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  async function handleStep1() {
    setBusy(true)
    setError(null)
    try {
      if (orgName.trim()) {
        await clientApi.updateOrg(orgName.trim())
      }
      setStep(2)
    } catch (e: any) {
      setError(e?.message || 'Failed')
    } finally {
      setBusy(false)
    }
  }

  async function handleStep2() {
    setBusy(true)
    setError(null)
    try {
      if (selectedPacks.size > 0) {
        await clientApi.selectPacks([...selectedPacks])
      }
      setStep(3)
    } catch (e: any) {
      setError(e?.message || 'Failed')
    } finally {
      setBusy(false)
    }
  }

  async function handleStep3() {
    setBusy(true)
    setError(null)
    try {
      if (selectedGroups.size > 0) {
        await clientApi.selectGroups([...selectedGroups])
      }
      setStep(4)
    } catch (e: any) {
      setError(e?.message || 'Failed')
    } finally {
      setBusy(false)
    }
  }

  async function handleAsk() {
    if (question.trim().length < 3) return
    setBusy(true)
    setError(null)
    try {
      const wl = getDefaultWatchlistId()
      await clientApi.askQuestion(question, wl || undefined)
      setAsked(true)
    } catch (e: any) {
      setError(e?.message || 'Failed')
    } finally {
      setBusy(false)
    }
  }

  async function finishOnboarding(dest: string = '/workspaces') {
    setBusy(true)
    setError(null)
    try {
      await clientApi.onboardingComplete()
      nav(dest)
    } catch (e: any) {
      setError(e?.message || 'Failed to complete onboarding')
    } finally {
      setBusy(false)
    }
  }

  const examples = [
    'Will the Fed cut rates before June 2026?',
    'Will NVIDIA maintain market cap lead through Q2?',
    'What is the probability of a US recession in 2026?',
  ]

  return (
    <div className="wizard">
      <div className="wizard-brand">
        <div className="brandMark">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 6v6l4 2" />
          </svg>
        </div>
        <div>
          <div className="brandName">Cenva Intelligence</div>
        </div>
      </div>

      <div className="wizard-progress">
        {[1, 2, 3, 4].map(s => (
          <div
            key={s}
            className={`wizard-dot${s === step ? ' active' : ''}${s < step ? ' done' : ''}`}
          />
        ))}
      </div>

      {/* Step 1: Welcome + Org Name */}
      {step === 1 && (
        <div className="wizard-step">
          <h2>Welcome to Cenva Intelligence</h2>
          <p className="muted">You'll work out of <strong>workspaces</strong> — living dashboards built around your questions, with a current read, evidence-backed forecasts, and live monitors. Let's set up your first one.</p>
          <div style={{ height: 16 }} />
          <label className="muted" style={{ fontSize: 13 }}>Organization name</label>
          <div style={{ height: 6 }} />
          <input
            className="input"
            placeholder="Acme Capital"
            value={orgName}
            onChange={e => setOrgName(e.target.value)}
            style={{ width: '100%' }}
          />
          {error && <div className="pill bad" style={{ marginTop: 12 }}>{error}</div>}
          <div className="wizard-actions">
            <button className="btn" onClick={() => setStep(2)}>Skip</button>
            <button className="btn primary" onClick={handleStep1} disabled={busy}>Continue</button>
          </div>
        </div>
      )}

      {/* Step 2: Select Packs */}
      {step === 2 && (
        <div className="wizard-step">
          <h2>Select Forecast Packs</h2>
          <p className="muted">Choose the packs you want to follow. You can change these later.</p>
          <div style={{ height: 16 }} />
          <div className="wizard-card-grid">
            {packs.map(p => (
              <div
                key={p.pack_id}
                className={`pack-card${selectedPacks.has(p.pack_id) ? ' selected' : ''}`}
                onClick={() => togglePack(p.pack_id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    togglePack(p.pack_id)
                  }
                }}
                role="button"
                tabIndex={0}
                aria-pressed={selectedPacks.has(p.pack_id)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <input
                    type="checkbox"
                    className="check"
                    checked={selectedPacks.has(p.pack_id)}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => togglePack(p.pack_id)}
                  />
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{p.name}</div>
                    {p.description && <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>{p.description}</div>}
                  </div>
                </div>
                <span className="pill" style={{ marginTop: 8 }}>{p.kind}</span>
              </div>
            ))}
            {!packsLoaded && <div className="muted">Loading packs...</div>}
            {packsLoaded && packsLoadError && (
              <div>
                <div className="pill bad">{packsLoadError}</div>
                <button
                  className="btn"
                  style={{ marginTop: 8 }}
                  onClick={() => {
                    setPacks([])
                    setPacksLoaded(false)
                    setPacksLoadError(null)
                  }}
                >
                  Retry
                </button>
              </div>
            )}
            {packsLoaded && !packsLoadError && packs.length === 0 && <div className="muted">No packs available yet.</div>}
          </div>
          {error && <div className="pill bad" style={{ marginTop: 12 }}>{error}</div>}
          <div className="wizard-actions">
            <button className="btn" onClick={() => setStep(1)}>Back</button>
            <button className="btn" onClick={() => setStep(3)}>Skip</button>
            <button className="btn primary" onClick={handleStep2} disabled={busy}>Continue</button>
          </div>
        </div>
      )}

      {/* Step 3: Select Company Groups */}
      {step === 3 && (
        <div className="wizard-step">
          <h2>Select Company Groups</h2>
          <p className="muted">Track groups of companies for intelligence updates.</p>
          <div style={{ height: 16 }} />
          <div className="wizard-card-grid">
            {groups.map(g => (
              <div
                key={g.group_id}
                className={`group-card${selectedGroups.has(g.group_id) ? ' selected' : ''}`}
                onClick={() => toggleGroup(g.group_id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    toggleGroup(g.group_id)
                  }
                }}
                role="button"
                tabIndex={0}
                aria-pressed={selectedGroups.has(g.group_id)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <input
                    type="checkbox"
                    className="check"
                    checked={selectedGroups.has(g.group_id)}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => toggleGroup(g.group_id)}
                  />
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{g.name}</div>
                    {g.description && <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>{g.description}</div>}
                  </div>
                </div>
              </div>
            ))}
            {!groupsLoaded && <div className="muted">Loading groups...</div>}
            {groupsLoaded && groupsLoadError && (
              <div>
                <div className="pill bad">{groupsLoadError}</div>
                <button
                  className="btn"
                  style={{ marginTop: 8 }}
                  onClick={() => {
                    setGroups([])
                    setGroupsLoaded(false)
                    setGroupsLoadError(null)
                  }}
                >
                  Retry
                </button>
              </div>
            )}
            {groupsLoaded && !groupsLoadError && groups.length === 0 && <div className="muted">No groups available yet.</div>}
          </div>
          {error && <div className="pill bad" style={{ marginTop: 12 }}>{error}</div>}
          <div className="wizard-actions">
            <button className="btn" onClick={() => setStep(2)}>Back</button>
            <button className="btn" onClick={() => setStep(4)}>Skip</button>
            <button className="btn primary" onClick={handleStep3} disabled={busy}>Continue</button>
          </div>
        </div>
      )}

      {/* Step 4: Ask First Question */}
      {step === 4 && (
        <div className="wizard-step">
          <h2>Create your first workspace</h2>
          <p className="muted">Ask a question to see a quick forecast, or jump straight into building a full workspace around it.</p>
          <div style={{ height: 16 }} />

          {!asked ? (
            <>
              <div className="ask-input-row">
                <input
                  className="input ask-input"
                  placeholder="Will the Fed cut rates before June 2026?"
                  value={question}
                  onChange={e => setQuestion(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleAsk()}
                />
                <button className="btn primary ask-btn" onClick={handleAsk} disabled={busy || question.trim().length < 3}>
                  {busy ? <span className="ask-spinner" /> : 'Ask'}
                </button>
              </div>
              <div style={{ height: 12 }} />
              <div className="muted" style={{ fontSize: 12 }}>Try one of these:</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
                {examples.map((ex, i) => (
                  <button
                    key={i}
                    className="btn"
                    style={{ fontSize: 12, padding: '6px 10px' }}
                    onClick={() => setQuestion(ex)}
                  >
                    {ex}
                  </button>
                ))}
              </div>
            </>
          ) : (
            <div className="ask-done">
              <span className="pill ok">Question submitted</span>
              <span className="muted" style={{ fontSize: 13 }}>Your forecast is being generated. You'll find it and all your workspaces on the Workspaces home.</span>
            </div>
          )}

          {error && <div className="pill bad" style={{ marginTop: 12 }}>{error}</div>}
          <div className="wizard-actions">
            <button className="btn" onClick={() => setStep(3)}>Back</button>
            <button className="btn" onClick={() => finishOnboarding('/workspaces/new')} disabled={busy}>
              Build a full workspace
            </button>
            <button className="btn primary" onClick={() => finishOnboarding('/workspaces')} disabled={busy}>
              Go to Workspaces
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
